import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { PasswordInput } from "./PasswordInput";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";

export function ChangePasswordModal({ open, onOpenChange, hasPassword = true }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
  };

  const handleOpenChange = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters long");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("New password and confirmation do not match");
      return;
    }
    setLoading(true);
    try {
      await api.put("/auth/change-password", {
        current_password: hasPassword ? currentPassword : undefined,
        new_password: newPassword,
      });
      toast.success("Password updated successfully");
      handleOpenChange(false);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="rounded-sm" data-testid="change-password-modal">
        <DialogHeader>
          <DialogTitle className="uppercase text-brand-navy">Change Password</DialogTitle>
          <DialogDescription>
            {hasPassword
              ? "Enter your current password and choose a new one."
              : "Your account signs in via Microsoft. Set a password to also enable email/password login."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          {error && (
            <p className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2" data-testid="change-password-error">
              {error}
            </p>
          )}
          {hasPassword && (
            <div className="space-y-2">
              <Label htmlFor="current-password">Current Password</Label>
              <PasswordInput
                id="current-password"
                data-testid="current-password-input"
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="rounded-sm bg-white border-slate-300"
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="new-password">New Password</Label>
            <PasswordInput
              id="new-password"
              data-testid="new-password-input"
              required
              minLength={8}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="rounded-sm bg-white border-slate-300"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm New Password</Label>
            <PasswordInput
              id="confirm-password"
              data-testid="confirm-password-input"
              required
              minLength={8}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="rounded-sm bg-white border-slate-300"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="change-password-submit-button"
            className="w-full rounded-sm bg-brand-navy text-white hover:bg-brand-navy-light font-bold uppercase tracking-wide text-sm"
          >
            {loading ? "Updating..." : "Update Password"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
